// Phase-0 spike — Soniox realtime + diarization.
//
// Soniox is the potential pure-browser, NO-SERVER path: the key is sent in the
// first WebSocket config message, and WebSockets aren't subject to CORS. So the
// browser can pass the user's OWN long-lived key straight to the socket and skip
// token-minting entirely (the "direct" path). We also test the documented
// temp-key path for comparison. Speaker label lives at tokens[].speaker.

import type { LiveSession, SessionEvents, Word } from './types'

const WS_URL = 'wss://stt-rt.soniox.com/transcribe-websocket'
const TMP_KEY_URL = 'https://api.soniox.com/v1/auth/temporary-api-key'

export async function mintSonioxTempKey(apiKey: string): Promise<string> {
  const res = await fetch(TMP_KEY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      usage_type: 'transcribe_websocket',
      expires_in_seconds: 3600
    })
  })
  if (!res.ok) {
    throw new Error(`temp key failed: HTTP ${res.status} ${await res.text()}`)
  }
  const json = (await res.json()) as { api_key?: string }
  if (!json.api_key) throw new Error('temp key: no api_key in response')
  return json.api_key
}

interface SonioxToken {
  text: string
  speaker?: string | number
  is_final?: boolean
}
interface SonioxMessage {
  tokens?: SonioxToken[]
  error_code?: string
  error_message?: string
}

export interface ConnectSonioxOpts {
  /** Either the user's own key (direct path) or a minted temp key. */
  key: string
  sampleRate: number
}

export function connectSoniox(
  opts: ConnectSonioxOpts,
  ev: SessionEvents
): LiveSession {
  const ws = new WebSocket(WS_URL)
  ws.binaryType = 'arraybuffer'

  let ready = false
  const queue: ArrayBuffer[] = []
  const finalWords: Word[] = []

  ev.onState('connecting', 'dialing stt-rt.soniox.com')

  ws.onopen = () => {
    ev.onLog('ws open — sending config (model stt-rt-v5, diarization on)')
    ws.send(
      JSON.stringify({
        api_key: opts.key,
        model: 'stt-rt-v5',
        audio_format: 'pcm_s16le',
        sample_rate: opts.sampleRate,
        num_channels: 1,
        enable_speaker_diarization: true,
        language_hints: ['en']
      })
    )
    ready = true
    ev.onState('live', 'config sent')
    for (const buf of queue) ws.send(buf)
    queue.length = 0
  }

  ws.onmessage = (e: MessageEvent<string>) => {
    const msg = JSON.parse(e.data) as SonioxMessage
    if (msg.error_code) {
      ev.onState('error', `${msg.error_code}: ${msg.error_message ?? ''}`)
      return
    }
    const interim: Word[] = []
    for (const t of msg.tokens ?? []) {
      if (!t.text) continue
      const word: Word = {
        text: t.text,
        speaker: t.speaker !== undefined ? String(t.speaker) : '?',
        isFinal: Boolean(t.is_final)
      }
      if (word.isFinal) finalWords.push(word)
      else interim.push(word)
    }
    ev.onWords(finalWords, interim)
  }

  ws.onerror = () => ev.onState('error', 'websocket error')
  ws.onclose = (e) => {
    if (ready) ev.onState('closed', `socket closed (${e.code})`)
    else ev.onState('error', `closed before config (${e.code})`)
  }

  return {
    sendPcm(pcm) {
      if (!ready) {
        queue.push(pcm)
        return
      }
      if (ws.readyState === WebSocket.OPEN) ws.send(pcm)
    },
    stop() {
      // An empty binary frame signals end-of-audio; the server then finalizes.
      if (ws.readyState === WebSocket.OPEN) {
        ev.onState('closing', 'sending end-of-audio')
        ws.send(new ArrayBuffer(0))
        ws.close()
      } else {
        ws.close()
      }
    }
  }
}
